import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import levelUpHeartLeftImg from '../../assets/levelup_heart_left.png';
import levelUpBrainRightImg from '../../assets/levelup_brain_right.png';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

function ActionButton({ label, onClick, disabled = false, className = '' }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`relative h-[68px] w-[220px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99] disabled:cursor-default disabled:opacity-45 sm:w-[250px] ${className}`}
      style={{ fontFamily: 'Karma, serif' }}
    >
      <img
        src={loreButton}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
      <span className="relative z-[1] flex h-full w-full items-center justify-center px-5 text-center text-[17px] font-semibold leading-[1.05] translate-y-[2px] sm:text-[18px]">
        {label}
      </span>
    </button>
  );
}

export default function LevelUpModal({
  open,
  levelUpStep,
  playerLevel,
  levelUpPulse,
  onChooseHp,
  onChooseSp,
  onContinue,
}) {
  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[6000] flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <motion.div
            className="relative z-[1] w-[920px] max-w-[94vw] bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Karma, serif' }}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div className="relative z-[1] mx-auto flex max-h-[82svh] w-full max-w-[820px] flex-col items-center pt-6 sm:pt-8">
              <h2 className="text-center text-[18px] font-semibold text-white sm:text-[20px]">
                {levelUpStep === 'choice' ? 'Level up!' : 'Extra bonus unlocked:'}
              </h2>

              <div className="mt-3 max-w-[620px] text-center text-[18px] leading-[1.25] text-white/90">
                {levelUpStep === 'choice'
                  ? `You are now level ${Math.max(1, Number(playerLevel) || 1) + 1}. Choose one upgrade:`
                  : 'Choose your reward:'}
              </div>

              <div className="mt-6 w-full overflow-visible">
                {levelUpStep === 'choice' ? (
                  <>
                    <div className="hidden md:flex items-center justify-center gap-6 overflow-visible">
                      <ActionButton
                        label="Increase Health"
                        onClick={onChooseHp}
                        disabled={!!levelUpPulse}
                      />

                      <div className="relative z-0 flex items-center justify-center w-[240px] h-[240px] shrink-0">
                        <motion.img
                          src={levelUpHeartLeftImg}
                          alt=""
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                          animate={
                            levelUpPulse === 'hp'
                              ? {
                                  scale: [1, 1.12, 1],
                                  filter: [
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                    'drop-shadow(0 0 18px rgba(220,38,38,0.65))',
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                  ],
                                }
                              : { scale: 1, filter: 'drop-shadow(0 0 0px rgba(0,0,0,0))' }
                          }
                          transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />

                        <motion.img
                          src={levelUpBrainRightImg}
                          alt=""
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                          animate={
                            levelUpPulse === 'sp'
                              ? {
                                  scale: [1, 1.12, 1],
                                  filter: [
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                    'drop-shadow(0 0 18px rgba(140,120,255,0.65))',
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                  ],
                                }
                              : { scale: 1, filter: 'drop-shadow(0 0 0px rgba(0,0,0,0))' }
                          }
                          transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                      </div>

                      <ActionButton
                        label="Increase Sanity"
                        onClick={onChooseSp}
                        disabled={!!levelUpPulse}
                      />
                    </div>

                    <div className="md:hidden flex flex-col items-center overflow-visible">
                      <div className="relative z-0 flex items-center justify-center w-[300px] h-[300px] max-w-full">
                        <motion.img
                          src={levelUpHeartLeftImg}
                          alt=""
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                          animate={
                            levelUpPulse === 'hp'
                              ? {
                                  scale: [1, 1.12, 1],
                                  filter: [
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                    'drop-shadow(0 0 18px rgba(255, 13, 0, 0.6))',
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                  ],
                                }
                              : { scale: 1, filter: 'drop-shadow(0 0 0px rgba(0,0,0,0))' }
                          }
                          transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />

                        <motion.img
                          src={levelUpBrainRightImg}
                          alt=""
                          draggable={false}
                          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                          animate={
                            levelUpPulse === 'sp'
                              ? {
                                  scale: [1, 1.12, 1],
                                  filter: [
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                    'drop-shadow(0 0 18px rgba(140,120,255,0.65))',
                                    'drop-shadow(0 0 0px rgba(0,0,0,0))',
                                  ],
                                }
                              : { scale: 1, filter: 'drop-shadow(0 0 0px rgba(0,0,0,0))' }
                          }
                          transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                      </div>

                      <div className="-mt-2 flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center">
                        <ActionButton
                          label="Increase Health"
                          onClick={onChooseHp}
                          disabled={!!levelUpPulse}
                        />
                        <ActionButton
                          label="Increase Sanity"
                          onClick={onChooseSp}
                          disabled={!!levelUpPulse}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="w-full mt-2 flex flex-col items-center justify-center gap-4 max-h-[72svh] overflow-y-auto pr-1"
                    style={{ touchAction: 'pan-y' }}
                    onWheelCapture={(e) => e.stopPropagation()}
                    onTouchStartCapture={(e) => e.stopPropagation()}
                    onTouchMoveCapture={(e) => e.stopPropagation()}
                  >
                    <img
                      src="/ui/levelup_draw_extra_card.png"
                      alt="Draw one extra card at the beginning of combat."
                      draggable={false}
                      className="w-auto max-w-[92vw] max-h-[40vh] object-contain block"
                    />

                    <div className="max-w-[520px] text-center text-[18px] leading-[1.25] text-white/85">
                      Draw one extra card at the beginning of combat.
                    </div>

                    <ActionButton label="Continue" onClick={onContinue} />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
